import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { RefreshCw, BarChart3, Users, FileText, CheckCircle, AlertCircle } from 'lucide-react';
import { recalculateStats } from '../../utils/updateStats';
import { getRealStats } from '../../utils/realStats';

const StatsManager = () => {
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [result, setResult] = useState(null);
  const [currentStats, setCurrentStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleRecalculateStats = async () => {
    setIsRecalculating(true);
    setResult(null);
    
    try {
      const recalcResult = await recalculateStats();
      
      if (recalcResult.success) {
        setResult({
          success: true,
          message: 'Stats recalculated successfully!',
          data: recalcResult.data
        });
        
        // Fetch updated stats
        await fetchCurrentStats();
      } else {
        setResult({
          success: false,
          message: 'Failed to recalculate stats: ' + recalcResult.error
        });
      }
    } catch (error) {
      setResult({
        success: false,
        message: 'Error recalculating stats: ' + error.message
      });
    } finally {
      setIsRecalculating(false);
    }
  };

  const fetchCurrentStats = async () => {
    setLoading(true);
    try {
      const statsResult = await getRealStats();
      if (statsResult.success) {
        setCurrentStats(statsResult.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch stats on component mount
  React.useEffect(() => {
    fetchCurrentStats();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Statistics Manager
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage and update homepage statistics
          </p>
        </div>

        {/* Current Stats Display */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Current Homepage Statistics
            </h2>
            <button
              onClick={fetchCurrentStats}
              disabled={loading}
              className="btn-secondary inline-flex items-center"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          {currentStats ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <Users className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-blue-600 mb-1">
                  {currentStats.students}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Students
                </div>
              </div>

              <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <FileText className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-green-600 mb-1">
                  {currentStats.mockTests}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Mock Tests
                </div>
              </div>

              <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                <BarChart3 className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-purple-600 mb-1">
                  {currentStats.successRate}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Success Rate
                </div>
              </div>

              <div className="text-center p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                <CheckCircle className="w-8 h-8 text-orange-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-orange-600 mb-1">
                  {currentStats.support}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Support
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="animate-pulse text-gray-400">Loading statistics...</div>
            </div>
          )}
        </div>

        {/* Recalculate Stats Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Recalculate Statistics
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            This will recalculate the user count and test statistics by querying the database directly.
            Use this when the homepage statistics are not updating automatically.
          </p>

          <button
            onClick={handleRecalculateStats}
            disabled={isRecalculating}
            className="btn-primary inline-flex items-center"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isRecalculating ? 'animate-spin' : ''}`} />
            {isRecalculating ? 'Recalculating...' : 'Recalculate Stats'}
          </button>

          {/* Result Message */}
          {result && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`mt-4 p-4 rounded-lg border ${
                result.success
                  ? 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-200'
                  : 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-200'
              }`}
            >
              <div className="flex items-center">
                {result.success ? (
                  <CheckCircle className="w-5 h-5 mr-2" />
                ) : (
                  <AlertCircle className="w-5 h-5 mr-2" />
                )}
                <span className="font-medium">{result.message}</span>
              </div>
              
              {result.success && result.data && (
                <div className="mt-2 text-sm">
                  <p>Updated statistics:</p>
                  <ul className="list-disc list-inside mt-1">
                    <li>Users: {result.data.userCount || 'N/A'}</li>
                    <li>Mock Tests: {result.data.mockTestCount || 'N/A'}</li>
                    <li>Tests: {result.data.testCount || 'N/A'}</li>
                    <li>Total Tests: {result.data.totalTestCount || 'N/A'}</li>
                  </ul>
                </div>
              )}
            </motion.div>
          )}
        </div>

        {/* Instructions */}
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 mt-8">
          <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">
            How it works
          </h3>
          <div className="text-blue-800 dark:text-blue-200 text-sm space-y-2">
            <p>
              • The homepage displays statistics from the <code>publicStats/homePageStats</code> document
            </p>
            <p>
              • Firebase Functions automatically update these stats when users register or are deleted
            </p>
            <p>
              • If the automatic updates aren't working, use the "Recalculate Stats" button above
            </p>
            <p>
              • The recalculation queries the actual collections and updates the stats document
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatsManager;